import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Button from '@hellodarwin/core/lib/components/common/button';
import ModalConfirm from '@hellodarwin/core/lib/components/common/hd-modal/confirm';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { usePlatform } from '@hellodarwin/core/lib/features/providers/platform-provider';
import Check from '@hellodarwin/icons/dist/icons/Check';
import Close from '@hellodarwin/icons/dist/icons/Close';
import Delete from '@hellodarwin/icons/dist/icons/Delete';
import Doc from '@hellodarwin/icons/dist/icons/Doc';
import Edit from '@hellodarwin/icons/dist/icons/Edit';
import Employees from '@hellodarwin/icons/dist/icons/Employees';
import Enterprise from '@hellodarwin/icons/dist/icons/Enterprise';
import { Input } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { AssistantStatus } from '../../../constants/assistant.constants';
import { useAssistantApi } from '../../../hooks/useAssistantApi';
import { useAssistantContext } from '../../../providers';
import { setAssistantStatus } from '../../../redux';
import { useAssistantDispatch, useAssistantSelector, } from '../../../redux/assistant-hooks';
import { deleteConversation, setActiveConversation, } from '../../../redux/assistantConversationSlice';
import { selectConversationLastActivity, selectConversationRequiresWaitNotice, selectConversationTitle, selectScopedConversationCount, } from '../../../redux/selectors';
import { getConversation, updateConversationTitle, } from '../../../redux/thunks/conversationThunks';
import formatRelativeTime from '../../../utils/formatRelativeTime';
import { ConversationContextBadge, ConversationContextBadges, ConversationContextValue, ConversationItemActions, ConversationItemContainer, ConversationItemContent, ConversationProcessingBadge, } from './styles';
const AssistantConversationItem = ({ conversation, isActive, }) => {
    var _a, _b, _c, _d, _e;
    const dispatch = useAssistantDispatch();
    const assistantApi = useAssistantApi();
    const theme = useTheme();
    const { t } = useTranslation();
    const { selectedLocale } = useLocale();
    const { app } = usePlatform();
    const { usageContext, grantProfileNamesById } = useAssistantContext();
    const scopedCompanyId = (_a = usageContext === null || usageContext === void 0 ? void 0 : usageContext.company_id) !== null && _a !== void 0 ? _a : null;
    const scopedGrantApplicationId = (_b = usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_application_id) !== null && _b !== void 0 ? _b : null;
    const [isEditing, setIsEditing] = useState(false);
    const [editingTitle, setEditingTitle] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const inputRef = useRef(null);
    const handleClick = (e) => {
        e.stopPropagation();
        dispatch(setActiveConversation(conversation.id));
        dispatch(setAssistantStatus(AssistantStatus.IDLE));
        dispatch(getConversation({
            api: assistantApi,
            conversationId: conversation.id,
        }));
    };
    const rawTitle = useAssistantSelector((state) => selectConversationTitle(state, conversation.id));
    const derivedTitle = rawTitle || t('assistant|untitled_conversation');
    const totalConversations = useAssistantSelector((state) => selectScopedConversationCount(state, scopedCompanyId, scopedGrantApplicationId));
    const isDeleteDisabled = conversation.isDeleting || totalConversations <= 1;
    const lastActivity = useAssistantSelector((state) => selectConversationLastActivity(state, conversation.id));
    const conversationRequiresWaitNotice = useAssistantSelector((state) => selectConversationRequiresWaitNotice(state, conversation.id));
    const companyId = (_c = conversation.usage_context) === null || _c === void 0 ? void 0 : _c.company_id;
    const grantApplicationId = (_d = conversation.usage_context) === null || _d === void 0 ? void 0 : _d.grant_application_id;
    const grantProfileId = (_e = conversation.usage_context) === null || _e === void 0 ? void 0 : _e.grant_profile_id;
    const grantProfileName = grantProfileId
        ? grantProfileNamesById === null || grantProfileNamesById === void 0 ? void 0 : grantProfileNamesById[grantProfileId]
        : undefined;
    const applicationDisplay = conversation.application_display ||
        conversation.program_id ||
        grantApplicationId;
    const isScopedAssistant = !!(usageContext === null || usageContext === void 0 ? void 0 : usageContext.company_id) ||
        !!(usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_application_id) ||
        !!(usageContext === null || usageContext === void 0 ? void 0 : usageContext.grant_profile_id);
    const showCompanyContext = !isScopedAssistant &&
        app === 'admin' &&
        !!companyId &&
        !!conversation.company_name;
    const showApplicationContext = !isScopedAssistant && !!grantApplicationId && !!applicationDisplay;
    const showProfileContext = !!grantProfileId && !!grantProfileName;
    const actionButtonStyle = {
        width: theme.spacing.lg,
        height: theme.spacing.lg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
    const handleDeleteClick = (e) => {
        e.stopPropagation();
        if (isDeleteDisabled)
            return;
        ModalConfirm({
            onOk: async () => {
                dispatch(deleteConversation({
                    api: assistantApi,
                    conversationId: conversation.id,
                }));
            },
            title: t('assistant|delete_conversation_title'),
            content: t('assistant|delete_conversation_message', {
                title: derivedTitle,
            }),
        });
    };
    const handleEditClick = (e) => {
        e.stopPropagation();
        setEditingTitle(derivedTitle);
        setIsEditing(true);
    };
    const handleSaveTitle = async () => {
        const trimmed = editingTitle.trim();
        if (!trimmed || isUpdating)
            return;
        if (trimmed === derivedTitle) {
            setIsEditing(false);
            return;
        }
        setIsUpdating(true);
        try {
            await dispatch(updateConversationTitle({
                api: assistantApi,
                conversationId: conversation.id,
                title: trimmed,
            })).unwrap();
            setIsEditing(false);
        }
        catch (error) {
            console.error('Failed to update conversation title:', error);
        }
        finally {
            setIsUpdating(false);
        }
    };
    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingTitle('');
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveTitle();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelEdit();
        }
    };
    useEffect(() => {
        var _a;
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            (_a = inputRef.current.input) === null || _a === void 0 ? void 0 : _a.select();
        }
    }, [isEditing]);
    return (_jsx(_Fragment, { children: _jsxs(ConversationItemContainer, { "$isActive": isActive, onClick: handleClick, children: [_jsxs(ConversationItemContent, { children: [(showCompanyContext ||
                            showApplicationContext ||
                            showProfileContext) && (_jsxs(ConversationContextBadges, { children: [showProfileContext && (_jsxs(ConversationContextBadge, { "$variant": "profile", children: [_jsx(Employees, { size: theme.spacing.md, color: theme.colors.green_dark }), _jsx(ConversationContextValue, { "$variant": "profile", title: grantProfileName, children: grantProfileName })] })), showCompanyContext && (_jsxs(ConversationContextBadge, { "$variant": "company", children: [_jsx(Enterprise, { size: 16, color: theme.colors.purple_2 }), _jsx(ConversationContextValue, { "$variant": "company", title: conversation.company_name || undefined, children: conversation.company_name })] })), showApplicationContext && (_jsxs(ConversationContextBadge, { "$variant": "application", children: [_jsx(Doc, { size: 16, color: theme.colors.orange_1 }), _jsx(ConversationContextValue, { "$variant": "application", title: applicationDisplay || undefined, children: applicationDisplay })] }))] })), isEditing ? (_jsx(Input, { ref: inputRef, value: editingTitle, onChange: (e) => setEditingTitle(e.target.value), onKeyDown: handleKeyDown, maxLength: 255, size: "small", style: {
                                fontSize: theme.fonts.body3,
                                fontWeight: theme.fontWeights.medium,
                            }, disabled: isUpdating, onClick: (e) => e.stopPropagation() })) : (_jsx(Typography, { elementTheme: "body2", medium: true, children: derivedTitle })), conversationRequiresWaitNotice && (_jsx(ConversationProcessingBadge, { children: t('assistant|conversation_processing_badge') })), _jsx(Typography, { nowrap: true, elementTheme: "body3", color: theme.colors.grey_2, children: formatRelativeTime(lastActivity, selectedLocale, true) })] }), _jsx(ConversationItemActions, { children: isEditing ? (_jsxs(_Fragment, { children: [_jsx(Button, { size: "circle", transparent: true, defaultStyle: theme.colors.green_1, headingIcon: _jsx(Check, { size: 18 }), style: actionButtonStyle, onClick: (e) => {
                                    e.stopPropagation();
                                    handleSaveTitle();
                                }, disabled: isUpdating ||
                                    !editingTitle.trim() ||
                                    editingTitle.trim() === derivedTitle }), _jsx(Button, { size: "circle", transparent: true, defaultStyle: theme.colors.grey_1, headingIcon: _jsx(Close, { size: 18 }), style: actionButtonStyle, onClick: (e) => {
                                    e.stopPropagation();
                                    handleCancelEdit();
                                }, disabled: isUpdating })] })) : (_jsxs(_Fragment, { children: [_jsx(Button, { size: "circle", transparent: true, style: actionButtonStyle, defaultStyle: isActive ? theme.colors.purple_1 : theme.colors.grey_1, headingIcon: _jsx(Edit, { size: 18 }), onClick: handleEditClick }), _jsx(Button, { size: "circle", transparent: true, style: actionButtonStyle, defaultStyle: isActive ? theme.colors.purple_1 : theme.colors.grey_1, headingIcon: _jsx(Delete, { size: 18 }), onClick: handleDeleteClick, disabled: isDeleteDisabled })] })) })] }, conversation.id) }));
};
export default AssistantConversationItem;
